import { useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Queries } from '@shared/api';
import { useWorkflowEntityEvent } from '@shared/data/cards/hooks/WorkflowEntityEvents';
import { STATIC_COMPONENT_IDS } from '@shared/data/entity/staticComponentIds';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isRelatedCardComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { SUBTABLE_ROW_LIMIT } from './subtableRowLimit';

export type SubtableRowRefs = NonNullable<API.ListWorkflowEntitiesQuery['list']['refs']>;

/** A subtable field's rows, loaded or drafts on the Create form. */
export interface SubtableRows {
  rows: API.WorkflowEntityFragment[];
  refs?: SubtableRowRefs;
  totalResults: number;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  /** Reloads the rows, after one is added, saved or deleted. */
  refetch: () => void;
  loading: boolean;
  error?: Error;
  workflowTemplate?: API.WorkflowTemplateFragment;
}

/**
 * Rows per page. Matches the backend's per-parent row limit, so one page fetches every row; the
 * cursor paging stays in case that limit rises.
 */
const PAGE_SIZE = SUBTABLE_ROW_LIMIT;

const SORT_BY_CREATED_AT: API.SortByComponent[] = [
  { componentId: STATIC_COMPONENT_IDS.CREATED_AT, direction: 'ASC' },
];

/**
 * The rows a parent card owns in a subtable: the entities of the subtable's workflow template
 * whose server-managed SUBTABLE_PARENT related-card column points at the parent, oldest first.
 */
export function useSubtableRows(component: API.SubtableComponent, entityId: string): SubtableRows {
  const subtableTemplateId = component.workflowTemplateId;
  const {
    workflowTemplate,
    loading: workflowTemplateLoading,
    error: workflowTemplateError,
  } = useWorkflowTemplate(subtableTemplateId);

  const parentComponentId = findSubtableParentComponentId(workflowTemplate?.components);

  const variables = useMemo(
    () => ({
      input: {
        workflowTemplateId: subtableTemplateId ?? '',
        limit: PAGE_SIZE,
        cursor: '',
        filterByComponents: [
          {
            componentId: parentComponentId ?? '',
            value: {
              containsCurrentUser: null,
              containsNumber: null,
              containsString: { values: [entityId], negate: false },
              dateIs: null,
              dateRange: null,
              dateTokenIs: null,
              isEmpty: null,
              numberIs: null,
              stringWildcard: null,
            },
          },
        ],
        sortByComponents: SORT_BY_CREATED_AT,
      },
    }),
    [subtableTemplateId, parentComponentId, entityId],
  );

  const { data, loading, error, refetch, fetchMore } = useQuery<
    API.ListWorkflowEntitiesQuery,
    API.ListWorkflowEntitiesQueryVariables
  >(Queries.workflow.listWorkflowEntities, {
    variables,
    skip: isNil(subtableTemplateId) || isNil(parentComponentId),
    fetchPolicy: 'cache-and-network',
  });

  useWorkflowEntityEvent(event => {
    if (event.event.entities.some(e => e.workflowTemplateId === subtableTemplateId)) {
      refetch();
    }
  });

  const rows = useMemo(() => data?.list.entities?.filter(notNil) ?? [], [data?.list.entities]);

  const list = data?.list;
  const loadMore = useCallback(async () => {
    if (!list?.hasMore || !list.cursor || loading) {
      return;
    }
    await fetchMore({
      variables: { ...variables, input: { ...variables.input, cursor: list.cursor } },
    });
  }, [list, loading, fetchMore, variables]);

  return {
    rows,
    refs: data?.list.refs ?? undefined,
    totalResults: data?.list.totalResults ?? rows.length,
    hasMore: !!data?.list.hasMore,
    loadMore,
    refetch,
    loading: workflowTemplateLoading || loading,
    error: workflowTemplateError ?? error,
    workflowTemplate,
  };
}

/** The server-managed related-card column on a subtable template that points at a row's parent card. */
export function findSubtableParentComponentId(
  components: API.AnyComponent[] | null | undefined,
): string | undefined {
  return components
    ?.filter(isRelatedCardComponent)
    .find(c => c.relatedCardType === 'SUBTABLE_PARENT')?.id;
}
